// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* -------------------------------------------------------------- */
  /* Logout (memoized, no navigation — caller decides)              */
  /* -------------------------------------------------------------- */
  const logout = useCallback(() => {
    localStorage.removeItem("arkila_admin_token");
    localStorage.removeItem("arkila_admin_data");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  }, []);

  /* -------------------------------------------------------------- */
  /* Login                                                           */
  /* -------------------------------------------------------------- */
  const login = useCallback((userData) => {
    const { token, admin } = userData || {};

    if (!token) {
      throw new Error("No token provided for login");
    }
    if (!admin) {
      throw new Error("No admin data provided for login");
    }

    localStorage.setItem("arkila_admin_token", token);
    localStorage.setItem("arkila_admin_data", JSON.stringify(admin));

    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    setUser(admin);
  }, []);

  /* -------------------------------------------------------------- */
  /* Update profile (for EditProfileModal)                          */
  /* -------------------------------------------------------------- */
  const updateProfile = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...updates };
      localStorage.setItem("arkila_admin_data", JSON.stringify(next));
      return next;
    });
  }, []);

  /* -------------------------------------------------------------- */
  /* Boot: validate token on mount                                  */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const token = localStorage.getItem("arkila_admin_token");
      const adminData = localStorage.getItem("arkila_admin_data");

      if (!token || !adminData) {
        setLoading(false);
        return;
      }

      try {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Optimistically show cached user
        let cached = null;
        try {
          cached = JSON.parse(adminData);
        } catch {
          /* corrupted — fall through */
        }
        if (cached && !cancelled) setUser(cached);

        // Verify with server + refresh profile
        try {
          const { data } = await api.get("/admin/me");
          if (cancelled) return;

          const fresh = data?.admin || data?.data || cached;
          if (fresh) {
            setUser(fresh);
            localStorage.setItem("arkila_admin_data", JSON.stringify(fresh));
          }
        } catch (error) {
          if (cancelled) return;
          if (error.response?.status === 401) {
            // Token invalid — clean up. Do NOT navigate here;
            // the axios interceptor already handles redirects.
            logout();
          }
          // Other errors (network, 500): keep cached user, don't logout
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, [logout]);

  /* -------------------------------------------------------------- */
  /* Context value                                                   */
  /* -------------------------------------------------------------- */
  const value = {
    user,          // current user object (for backward compat)
    admin: user,   // alias — Header reads `admin`
    login,
    logout,
    updateProfile,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;